import { useEffect } from 'react'
import {
  useQuery,
  useQueryClient,
  useMutation,
  useQueries,
  queryOptions,
} from '@tanstack/react-query'
import UserAvailability, {
  CustomSchedule,
  EventPriorityScheduling,
  MapStringEventPriorityScheduling,
  MapStringSeriesPriorityScheduling,
  ScheduleDraftView,
  SeriesPriorityScheduling,
  UpdateAvailabilityToggleRequest,
  UserAvailabilityToggleResponse,
  ProductFeature,
  SchedulePublishedViewSchedule,
  Schedule,
} from '@chilipiper/api-client/src/user-availability'
import * as R from 'remeda'
import { startOfWeek } from '@chilipiper/date-time'
import { useApiClient } from '@chilipiper/api-client/src/context'
import { useFireSessionContext } from '@chilipiper/fire-session'
import { defaultErrorCallback } from '@chilipiper/service/src/api'
import { DeepPartial, SchedulingLinkId } from './types'
import { queryKeyFactory as queryKeyFactoryMeetings } from './meetings'
import { queryKeyFactory as queryKeyFactoryExternalCalendar } from './external-calendar'

const prioritySettingsQueryKey = 'user-priority-settings'
const seriesSettingQueryKey = 'series-setting'
const seriesSettingsQueryKey = 'series-settings'
const availabilitySchedulesQueryKey = 'user-availability-schedules'
const userAvailabilityQueryKey = 'user-availability'
const usersAvailabilityQueryKey = 'users-availability'
const syncedTimezoneQueryKey = 'synced-timezone'

export const queryKeyFactory = {
  prioritySettings: (ids: string[]) => [prioritySettingsQueryKey, ids],
  seriesSetting: (seriesId: string) => [seriesSettingQueryKey, seriesId],
  seriesSettings: (seriesIds: string[]) => [seriesSettingsQueryKey, seriesIds],
  availabilitySchedules: (tenantId: string, userId: string) => [
    availabilitySchedulesQueryKey,
    tenantId,
    userId,
    'published',
  ],
  availabilitySchedulesDraft: (userId: string) => [availabilitySchedulesQueryKey, 'draft', userId],
  userAvailability: (tenantId: string, userId: string) => [
    userAvailabilityQueryKey,
    tenantId,
    userId,
  ],
  availabilityOfManyUsers: (
    tenantId: string,
    query: Parameters<typeof UserAvailability.prototype.getAvailabilityOfManyUsers>[1]
  ) => [userAvailabilityQueryKey, tenantId, query],
  usersAvailabilities: (
    tenantId: string,
    query: Parameters<typeof UserAvailability.prototype.getUsersAvailabilities>[1]
  ) => [usersAvailabilityQueryKey, tenantId, query],
  meetingsAvailabilitySpan: (
    tenantId: string,
    query: Parameters<typeof UserAvailability.prototype.getMeetingsAvailabilitySpan>[1]
  ) => [usersAvailabilityQueryKey, tenantId, query],
  flexibleRoundRobinAvailabilities: (
    tenantId: string,
    query: Parameters<typeof UserAvailability.prototype.getAvailabilitiesForFlexibleRoundRobin>[1]
  ) => ['flexible-round-robin-availabilities', tenantId, query],
  syncedTimezone: (tenantId: string, userId: string) => [syncedTimezoneQueryKey, tenantId, userId],
  checkUsersAvailability: (
    tenantId: string,
    meetingTypeId?: string,
    userIds: string[] = [],
    intervalStartsAt?: string
  ) => ['check-users-availability', tenantId, meetingTypeId, ...userIds, intervalStartsAt],
  availableMeetingTypes: (tenantId: string, userId: string) => [
    'available-meeting-types',
    tenantId,
    userId,
  ],
}

const THREE_WEEKS_LENGTH_MILLISECONDS = 1814400000
const DEFAULT_START_TIME = startOfWeek(new Date()).toISOString()

export const usePrioritySettings = (
  eventsIds: string[],
  duration = `${THREE_WEEKS_LENGTH_MILLISECONDS}milliseconds`,
  startTime = DEFAULT_START_TIME
) => {
  const { userAvailability } = useApiClient()
  const { tenantId, userId } = useFireSessionContext()

  return useQuery({
    queryKey: queryKeyFactory.prioritySettings([...eventsIds, duration, startTime]),
    queryFn: () => {
      if (eventsIds.length === 0) {
        return Promise.resolve({} as MapStringEventPriorityScheduling)
      }
      return userAvailability
        .getTheListOfEventsWithPrioritySchedulingConfiguration(tenantId, userId, {
          duration,
          startTime,
        })
        .then(data => {
          return data.reduce((prev, curr) => {
            if (curr.priority.type === 'EventPriorityScheduling') {
              return {
                ...prev,
                [curr.eventId]: {
                  config: curr.priority.config,
                },
              }
            } else {
              return prev
            }
          }, {})
        })
        .catch(e => {
          if (e.status === 404) {
            return {} as MapStringEventPriorityScheduling
          }

          throw e
        })
    },
    retry: (_count, error) => 'status' in error && error.status !== 404,
    refetchOnWindowFocus: false,
  })
}

export const useSeriesPrioritySettingsBatch = (seriesIds: string[] = []) => {
  const { userAvailability } = useApiClient()
  const { tenantId, userId } = useFireSessionContext()

  const isEnabled = seriesIds.length > 0

  const result = useQuery<MapStringSeriesPriorityScheduling>({
    queryKey: queryKeyFactory.seriesSettings(seriesIds),
    queryFn: () => {
      return userAvailability.getApiV1TenantTenantidUserUseridPrioritySchedulingSeriesConfigBatch(
        tenantId,
        userId,
        {
          eventSeriesId: seriesIds,
        }
      )
    },
    enabled: seriesIds.length > 0,
    refetchOnWindowFocus: false,
  })

  return {
    ...result,
    isLoading: isEnabled ? result.isLoading : false,
  }
}

export const useSetPrioritySettings = (
  eventsIds: string[],
  range: { end: string; start: string; userIds: string[] }
) => {
  const queryClient = useQueryClient()
  const { userAvailability } = useApiClient()
  const { tenantId, userId } = useFireSessionContext()

  return useMutation({
    mutationFn: (updates: MapStringEventPriorityScheduling) => {
      return Promise.all(
        Object.keys(updates).map(eventId => {
          const data = updates[eventId]
          if (data) {
            return userAvailability.putApiV1TenantTenantidUserUseridEventEventidPrioritySchedulingConfig(
              tenantId,
              userId,
              eventId,
              data
            )
          }
        })
      )
    },
    onMutate: async updates => {
      const previousSettings = queryClient.getQueryData<MapStringEventPriorityScheduling>(
        queryKeyFactory.prioritySettings(eventsIds)
      )

      const updatesWithoutRemoved = R.omitBy(updates, R.isNullish)
      const updatedSettings = { ...previousSettings, ...updatesWithoutRemoved }

      queryClient.setQueryData(queryKeyFactory.prioritySettings(eventsIds), updatedSettings)

      return { previousSettings, updatedSettings }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.prioritySettings(eventsIds) })
      queryClient.invalidateQueries({
        queryKey: queryKeyFactoryExternalCalendar.listEvents(range),
      })
      return queryClient.invalidateQueries({
        queryKey: queryKeyFactoryMeetings.listMeetings(range),
      })
    },
    onError: (_error, _updates, context) => {
      queryClient.setQueryData(
        queryKeyFactory.prioritySettings(eventsIds),
        context?.previousSettings
      )
      return queryClient.invalidateQueries({
        queryKey: queryKeyFactory.prioritySettings(eventsIds),
      })
    },
  })
}

export const useSetEventSetting = (eventsIds: string[], range: { end: string; start: string }) => {
  const queryClient = useQueryClient()
  const { userAvailability } = useApiClient()
  const { tenantId, userId } = useFireSessionContext()

  return useMutation({
    mutationFn: ({ eventId, update }: { eventId: string; update: EventPriorityScheduling }) => {
      return userAvailability.putApiV1TenantTenantidUserUseridEventEventidPrioritySchedulingConfig(
        tenantId,
        userId,
        eventId,
        update
      )
    },
    onMutate: async ({ eventId, update }) => {
      const previousSettings = queryClient.getQueryData<MapStringEventPriorityScheduling>(
        queryKeyFactory.prioritySettings(eventsIds)
      )

      const updated = {
        [eventId]: update,
      }

      const updatesWithoutRemoved = R.omitBy(updated, R.isNullish)
      const updatedSettings = { ...previousSettings, ...updatesWithoutRemoved }

      queryClient.setQueryData(queryKeyFactory.prioritySettings(eventsIds), updatedSettings)

      return { previousSettings, updatedSettings }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.prioritySettings(eventsIds) })
      queryClient.invalidateQueries({
        queryKey: queryKeyFactoryExternalCalendar.listEvents({ ...range, userIds: [userId] }),
      })
      return queryClient.invalidateQueries({
        queryKey: queryKeyFactoryMeetings.listMeetings(range),
      })
    },
    onError: (_error, _updates, context) => {
      queryClient.setQueryData(
        queryKeyFactory.prioritySettings(eventsIds),
        context?.previousSettings
      )
      return queryClient.invalidateQueries({
        queryKey: queryKeyFactory.prioritySettings(eventsIds),
      })
    },
  })
}

export const useSeriesPrioritySetting = (eventSeriesId: string) => {
  const { userAvailability } = useApiClient()
  const { tenantId, userId } = useFireSessionContext()

  return useQuery({
    queryKey: queryKeyFactory.seriesSetting(eventSeriesId),
    queryFn: () => {
      return userAvailability.getPrioritySchedulingConfigurationByEventSeriesId(
        tenantId,
        userId,
        eventSeriesId
      )
    },
    enabled: !!eventSeriesId,
    refetchOnWindowFocus: false,
  })
}

export const useSetEventSeriesSettings = () => {
  const queryClient = useQueryClient()
  const { userAvailability } = useApiClient()
  const { tenantId, userId } = useFireSessionContext()

  return useMutation({
    mutationFn: ({
      eventSeriesId,
      config,
    }: {
      config: SeriesPriorityScheduling
      eventSeriesId: string
    }) => {
      return userAvailability.configurePrioritySchedulingForTheEventSeries(
        tenantId,
        userId,
        eventSeriesId,
        config
      )
    },
    onMutate: async variables => {
      const { config, eventSeriesId } = variables
      const previousSeriesSetting = queryClient.getQueryData(['series-setting', eventSeriesId])
      queryClient.setQueryData(queryKeyFactory.seriesSetting(eventSeriesId), config)
      return { previousSeriesSetting, updatedSeriesSetting: config }
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeyFactory.seriesSetting(variables.eventSeriesId),
      })
    },
    onError: (_, variables, context) => {
      queryClient.setQueryData(
        queryKeyFactory.seriesSetting(variables.eventSeriesId),
        context?.previousSeriesSetting
      )
    },
  })
}

export const useUpdateEventSeriesSettings = (seriesIds: string[]) => {
  const queryClient = useQueryClient()
  const { userAvailability } = useApiClient()
  const { tenantId, userId } = useFireSessionContext()

  return useMutation({
    mutationFn: ({
      eventSeriesId,
      config,
    }: {
      config: SeriesPriorityScheduling
      eventSeriesId: string
    }) => {
      return userAvailability.putApiV1TenantTenantidUserUseridEventseriesEventseriesidPrioritySchedulingConfig(
        tenantId,
        userId,
        eventSeriesId,
        config
      )
    },
    onMutate: async variables => {
      const { config, eventSeriesId } = variables
      const previousSeriesSetting = queryClient.getQueryData<MapStringSeriesPriorityScheduling>([
        'series-settings',
        seriesIds,
      ])

      const updatedSeriesSetting = R.set(
        R.clone(previousSeriesSetting ?? {}),
        eventSeriesId,
        config
      )

      queryClient.setQueryData(queryKeyFactory.seriesSettings(seriesIds), updatedSeriesSetting)
      return { previousSeriesSetting, updatedSeriesSetting }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeyFactory.seriesSettings(seriesIds) })
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(
        queryKeyFactory.seriesSettings(seriesIds),
        context?.previousSeriesSetting
      )
    },
  })
}

export const useDeletePrioritySettings = (
  eventsIds: string[],
  range: { end: string; start: string }
) => {
  const queryClient = useQueryClient()
  const { userAvailability } = useApiClient()
  const { tenantId, userId } = useFireSessionContext()

  return useMutation({
    mutationFn: (removedId: string) => {
      return userAvailability.deletePrioritySchedulingConfiguration(tenantId, userId, removedId)
    },
    onMutate: async removedId => {
      const previousSettings = queryClient.getQueryData<MapStringEventPriorityScheduling>(
        queryKeyFactory.prioritySettings(eventsIds)
      )

      const updatedSettings = R.omit(previousSettings ?? {}, [removedId])

      queryClient.setQueryData(queryKeyFactory.prioritySettings(eventsIds), updatedSettings)

      return { previousSettings, updatedSettings }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyFactory.prioritySettings(eventsIds) })
      queryClient.invalidateQueries({
        queryKey: queryKeyFactoryExternalCalendar.listEvents({ ...range, userIds: [userId] }),
      })
      return queryClient.invalidateQueries({
        queryKey: queryKeyFactoryMeetings.listMeetings(range),
      })
    },
    onError: (_error, _updates, context) => {
      queryClient.setQueryData(
        queryKeyFactory.prioritySettings(eventsIds),
        context?.previousSettings
      )
      return queryClient.invalidateQueries({
        queryKey: queryKeyFactory.prioritySettings(eventsIds),
      })
    },
  })
}

export interface TypedScheduleDraftView<
  Type extends
    SchedulePublishedViewSchedule['schedule'] = SchedulePublishedViewSchedule['schedule'],
> extends ScheduleDraftView {
  draft: {
    data: DeepPartial<Type>
  }
}

export interface TypedSchedulePublishedView<
  Type extends
    SchedulePublishedViewSchedule['schedule'] = SchedulePublishedViewSchedule['schedule'],
> extends SchedulePublishedViewSchedule {
  schedule: Type
}

export type DefaultAvailabilitySchedule = {
  draft?: TypedScheduleDraftView<Schedule>
  id: string
  isDefault: true
  isUnpublishedDraft: boolean
  published?: TypedSchedulePublishedView<Schedule>
}

export type CustomAvailabilitySchedule = {
  draft: TypedScheduleDraftView<CustomSchedule>
  id: string
  isDefault: false
  isUnpublishedDraft: boolean
  published?: TypedSchedulePublishedView<CustomSchedule>
}

export type AvailabilitySchedule = DefaultAvailabilitySchedule | CustomAvailabilitySchedule

export const useAvailabilitySchedules = ({
  userId,
  compare,
}: {
  userId?: string
  compare?: boolean
} = {}) => {
  const { userAvailability } = useApiClient()
  const { tenantId, userId: currentUserId } = useFireSessionContext()

  const userIdToUse = userId ?? currentUserId

  const [
    { data: draftsData, isLoading: isLoadingDraft },
    { data: publishedData, isLoading: isLoadingPublished },
  ] = useQueries({
    queries: [
      queryOptions({
        queryKey: queryKeyFactory.availabilitySchedulesDraft(userIdToUse),
        queryFn: () => {
          return userAvailability
            .getApiV1TenantTenantidUserUseridAvailabilitySchedulesDrafts(tenantId, userIdToUse, {
              compare,
            })
            .catch(e => {
              if (e.status === 404) {
                return [] as ScheduleDraftView[]
              }

              throw e
            })
        },
        refetchOnWindowFocus: false,
      }),
      queryOptions({
        queryKey: queryKeyFactory.availabilitySchedules(tenantId, userIdToUse),
        queryFn: () => {
          return userAvailability
            .getApiV1TenantTenantidUserUseridAvailabilitySchedules(tenantId, userIdToUse)
            .catch(e => {
              if (e.status === 404) {
                return [] as SchedulePublishedViewSchedule[]
              }

              throw e
            })
        },
        refetchOnWindowFocus: false,
      }),
    ],
  })

  if (!draftsData || !publishedData || isLoadingDraft || isLoadingPublished) {
    return { data: undefined, isLoading: true }
  }

  // default availability is created as published and has no draft. we can have a draft without published
  // so we need to merge these ids to get the full list of ids and iterate
  const ids = R.unique([
    ...draftsData.map(draft => draft.id),
    ...publishedData.map(published => published.id),
  ])

  const publishedById = R.indexBy(publishedData, x => x.id)
  const draftById = R.indexBy(draftsData, x => x.id)
  const data = ids.map<AvailabilitySchedule>(id => {
    const draft = draftById[id]
    const published = publishedById[id]

    const isDefault = Boolean(published?.schedule.type === 'DefaultSchedule')

    if (isDefault) {
      const schedule: DefaultAvailabilitySchedule = {
        id,
        draft: {
          ...(draft ?? {}),
          draft: {
            ...draft?.draft,
            id,
            metadata: published?.metadata,
            data: draft?.draft ?? published?.schedule,
          },
        },
        published,
        isUnpublishedDraft: draft ? !R.isDeepEqual(draft.draft, published?.schedule) : false,
        isDefault: true,
      }

      return schedule
    }

    const schedule: CustomAvailabilitySchedule = {
      id,
      draft: {
        ...(draft ?? {}),
        draft: {
          ...draft?.draft,
          id,
          metadata: published?.metadata,
          data: draft?.draft ?? published?.schedule,
        },
      },
      published: published
        ? { ...published, schedule: published.schedule as CustomSchedule }
        : undefined,
      isUnpublishedDraft: draft ? !R.isDeepEqual(draft.draft, published?.schedule) : false,
      isDefault: false,
    }

    return schedule
  })

  const sortedData = R.sortBy(data, item => {
    if (item.isDefault) {
      return 0 // isDefault should be first
    } else {
      return Date.parse(
        (item.draft?.metadata?.updatedAt || item.published?.metadata.updatedAt) ?? ''
      ) // sort by the date property
    }
  })

  return { data: sortedData, isLoading: false }
}

export const useUserSyncedTimezone = (userId: string) => {
  const { userAvailability } = useApiClient()
  const { tenantId } = useFireSessionContext()

  return useQuery({
    queryKey: queryKeyFactory.syncedTimezone(tenantId, userId),
    queryFn: () => {
      return userAvailability.getUserSyncedTimezone(tenantId, userId)
    },
  })
}

export const useCreateAvailabilityScheduleDraft = ({ userId }: { userId?: string } = {}) => {
  const queryClient = useQueryClient()
  const { userAvailability } = useApiClient()
  const { tenantId, userId: currentUserId } = useFireSessionContext()

  const userIdToUse = userId ?? currentUserId

  return useMutation({
    mutationFn: (
      data: TypedScheduleDraftView['draft']['data'] | SchedulePublishedViewSchedule['schedule']
    ) => {
      if (data.type === 'CustomSchedule') {
        return userAvailability.postApiV1TenantTenantidUserUseridAvailabilitySchedulesDrafts(
          tenantId,
          userIdToUse,
          data
        )
      }
      return userAvailability.postApiV1TenantTenantidUserUseridAvailabilitySchedulesDrafts(
        tenantId,
        userIdToUse,
        {
          name: data.name,
          type: 'CustomSchedule',
          activeOn: { type: 'Always' },
          assignedTo: { type: 'NoAssignment' },
          isStrictlyReserved: false,
          tz: data.tz,
          workingHours: data.workingHours,
        }
      )
    },
    onSuccess: () => {
      void Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeyFactory.availabilitySchedulesDraft(userIdToUse),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeyFactory.availabilitySchedules(tenantId, userIdToUse),
        }),
      ])
    },
  })
}

export const useUpdateAvailabilityScheduleDraft = ({
  availabilityId,
  userId,
}: {
  availabilityId: string
  userId?: string
}) => {
  const queryClient = useQueryClient()
  const { userAvailability } = useApiClient()
  const { tenantId, userId: currentUserId } = useFireSessionContext()

  const userIdToUse = userId ?? currentUserId

  return useMutation({
    mutationFn: (data: TypedScheduleDraftView['draft']['data']) => {
      return userAvailability.putApiV1TenantTenantidUserUseridAvailabilitySchedulesDraftsScheduleid(
        tenantId,
        userIdToUse,
        availabilityId,
        data
      )
    },
    onMutate: async updates => {
      await queryClient.cancelQueries({
        queryKey: queryKeyFactory.availabilitySchedulesDraft(userIdToUse),
      })
      const previousSchedules = queryClient.getQueryData<ScheduleDraftView[]>(
        queryKeyFactory.availabilitySchedulesDraft(userIdToUse)
      )

      const updatedSchedules = previousSchedules?.map(schedule => {
        if (schedule.id === availabilityId) {
          return { ...schedule, draft: updates }
        }
        return schedule
      })
      queryClient.setQueryData(
        queryKeyFactory.availabilitySchedulesDraft(userIdToUse),
        updatedSchedules
      )
      return { previousSchedules, updatedSchedules }
    },
    onSuccess: () => {
      void Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeyFactory.availabilitySchedulesDraft(userIdToUse),
        }),
      ])
    },
    onError: (_error, _updates, context) => {
      queryClient.setQueryData(
        queryKeyFactory.availabilitySchedulesDraft(userIdToUse),
        context?.previousSchedules
      )
      return queryClient.invalidateQueries({
        queryKey: queryKeyFactory.availabilitySchedulesDraft(userIdToUse),
      })
    },
  })
}

export const usePublishAvailabilityScheduleDraft = ({
  availabilityId,
  userId,
}: {
  availabilityId: string
  userId?: string
}) => {
  const queryClient = useQueryClient()
  const { userAvailability } = useApiClient()
  const { tenantId, userId: currentUserId } = useFireSessionContext()

  const userIdToUse = userId ?? currentUserId

  return useMutation({
    mutationFn: (draft: TypedScheduleDraftView['draft']['data']) => {
      return userAvailability.putApiV1TenantTenantidUserUseridAvailabilitySchedulesScheduleid(
        tenantId,
        userIdToUse,
        availabilityId,
        draft as CustomSchedule
      )
    },
    onSuccess: () => {
      void Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeyFactory.availabilitySchedules(tenantId, userIdToUse),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeyFactory.availabilitySchedulesDraft(userIdToUse),
        }),
      ])
    },
  })
}

export const useDeleteAvailabilitySchedule = ({ userId }: { userId?: string } = {}) => {
  const queryClient = useQueryClient()
  const { userAvailability } = useApiClient()
  const { tenantId, userId: currentUserId } = useFireSessionContext()

  const userIdToUse = userId ?? currentUserId

  return useMutation({
    mutationFn: (data: AvailabilitySchedule) => {
      return Promise.all([
        userAvailability.deleteApiV1TenantTenantidUserUseridAvailabilitySchedulesDraftsScheduleid(
          tenantId,
          userIdToUse,
          data.id
        ),
        userAvailability
          .deleteApiV1TenantTenantidUserUseridAvailabilitySchedulesScheduleid(
            tenantId,
            userIdToUse,
            data.id
          )
          .catch(e => {
            if (e.status === 404) {
              return
            }

            throw e
          }),
      ])
    },
    onSuccess: () => {
      void Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeyFactory.availabilitySchedulesDraft(userIdToUse),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeyFactory.availabilitySchedules(tenantId, userIdToUse),
        }),
      ])
    },
  })
}

export const useCreateSingleUseLink = () => {
  const { userAvailabilityV2 } = useApiClient()
  const { tenantId, userId: sessionUserId } = useFireSessionContext()

  return useMutation({
    mutationFn: ({
      id,
      isHighPriority,
      hostId = sessionUserId,
      bookerId = sessionUserId,
      selectedMeetingTypeId,
    }: {
      bookerId?: string
      hostId?: string
      selectedMeetingTypeId?: string
      id: SchedulingLinkId
      isHighPriority?: boolean
    }) => {
      let productFeature = {} as ProductFeature

      switch (id.type) {
        case 'AdminIndividualSchedulingLink':
        case 'GroupSchedulingLink':
        case 'RoundRobinSchedulingLink':
        case 'OwnershipSchedulingLink':
          productFeature = {
            hostId: hostId,
            linkId: id.linkId,
            type: id.type,
            selectedMeetingTypeId,
          } satisfies ProductFeature
          break
        case 'PersonalIndividualSchedulingLink':
          productFeature = {
            hostId: hostId,
            meetingTypeId: id.meetingTypeId,
            type: 'PersonalIndividualSchedulingLink',
          } satisfies ProductFeature
          break
      }

      return userAvailabilityV2.createMeetingAttemptV2(tenantId, {
        bookerId: bookerId,
        productFeature: productFeature,
        features: {
          highPriorityMeeting: !!isHighPriority,
        },
      })
    },
  })
}

export const useUsersAvailabilities = (
  tenantId: string,
  query: Parameters<typeof UserAvailability.prototype.getUsersAvailabilities>[1],
  options?: { enabled?: boolean }
) => {
  const { userAvailability } = useApiClient()

  return useQuery({
    queryKey: queryKeyFactory.usersAvailabilities(tenantId, query),
    queryFn: () => {
      return userAvailability.getUsersAvailabilities(tenantId, query)
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!tenantId && options?.enabled,
  })
}
export const useGetAvailabilitySpan = (
  tenantId: string,
  query: Parameters<typeof UserAvailability.prototype.getMeetingsAvailabilitySpan>[1],
  options?: { enabled?: boolean }
) => {
  const { userAvailability } = useApiClient()

  return useQuery({
    queryKey: queryKeyFactory.meetingsAvailabilitySpan(tenantId, query),
    queryFn: () => {
      return userAvailability.getMeetingsAvailabilitySpan(tenantId, query)
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!tenantId && options?.enabled,
  })
}

export const useFlexibleRoundRobinAvailabilities = (
  tenantId: string,
  query: Parameters<typeof UserAvailability.prototype.getAvailabilitiesForFlexibleRoundRobin>[1],
  options?: { enabled?: boolean }
) => {
  const { userAvailability } = useApiClient()

  return useQuery({
    queryKey: queryKeyFactory.flexibleRoundRobinAvailabilities(tenantId, query),
    queryFn: () => {
      return userAvailability.getAvailabilitiesForFlexibleRoundRobin(tenantId, query)
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!tenantId && options?.enabled,
  })
}

export const useGetUserAvailability = (tenantId: string, userId?: string) => {
  const { userAvailability } = useApiClient()

  const query = useQuery({
    queryKey: queryKeyFactory.userAvailability(tenantId, userId ?? ''),
    queryFn: async () => {
      return userAvailability.getAvailabilityOfUser(tenantId, userId ?? '')
    },
    enabled: !!tenantId && !!userId,
  })

  useEffect(() => {
    if (query.isError) {
      defaultErrorCallback(query.error as any)
    }
  }, [query.isError, query.error])

  return query
}

export const useGetAvailabilityOfManyUsers = (
  tenantId: string,
  query: Parameters<typeof UserAvailability.prototype.getAvailabilityOfManyUsers>[1]
) => {
  const { userAvailability } = useApiClient()

  const queryResult = useQuery({
    queryKey: queryKeyFactory.availabilityOfManyUsers(tenantId, query),
    queryFn: async () => {
      return userAvailability.getAvailabilityOfManyUsers(tenantId, query)
    },
    enabled: !!tenantId,
  })

  useEffect(() => {
    if (queryResult.isError) {
      defaultErrorCallback(queryResult.error as any)
    }
  }, [queryResult.isError, queryResult.error])

  return queryResult
}

export const useSetUserAvailability = (tenantId: string, userId: string) => {
  const queryClient = useQueryClient()
  const { userAvailability } = useApiClient()

  return useMutation({
    mutationFn: async (data: UpdateAvailabilityToggleRequest) =>
      userAvailability.updateAvailabilityOfUser(tenantId, userId, data),
    onMutate: async (updates: UpdateAvailabilityToggleRequest) => {
      await queryClient.cancelQueries({
        queryKey: queryKeyFactory.userAvailability(tenantId, userId),
      })
      const previousUserAvailability = queryClient.getQueryData<UserAvailabilityToggleResponse>(
        queryKeyFactory.userAvailability(tenantId, userId)
      )
      if (previousUserAvailability) {
        const updatedUserAvailability: UserAvailabilityToggleResponse = {
          ...previousUserAvailability,
          type: 'LastConfigured',
          toggle: {
            tenantId,
            userId,
            status: updates.status,
          },
        }
        queryClient.setQueryData(
          queryKeyFactory.userAvailability(tenantId, userId),
          updatedUserAvailability
        )
        return { previousUserAvailability, updatedUserAvailability }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeyFactory.userAvailability(tenantId, userId),
      })
    },
  })
}

export const useCheckUsersAvailability = ({
  userIds,
  intervalStartsAt,
  meetingTypeId,
}: {
  intervalStartsAt?: string
  meetingTypeId?: string
  userIds?: string[]
}) => {
  const { tenantId } = useFireSessionContext()
  const { userAvailabilityV2 } = useApiClient()

  const enabled = Boolean(userIds?.length) && !!intervalStartsAt && !!meetingTypeId

  const query = useQuery({
    queryKey: queryKeyFactory.checkUsersAvailability(
      tenantId,
      meetingTypeId,
      userIds,
      intervalStartsAt
    ),
    queryFn: async () => {
      return userAvailabilityV2.checkUsersAvailabilityV2(tenantId, {
        userIds: userIds as string[],
        meetingStartTime: intervalStartsAt as string,
        meetingTypeId: meetingTypeId as string,
        meetingTypeVersion: new Date().toISOString(),
      })
    },
    enabled,
  })

  useEffect(() => {
    if (query.isError) {
      defaultErrorCallback(query.error as any)
    }
  }, [query.isError, query.error])

  return query
}

export const useAvailableMeetingTypes = (userId?: string) => {
  const { userAvailability } = useApiClient()
  const { tenantId, userId: currentUserId } = useFireSessionContext()

  const userIdToUse = userId ?? currentUserId

  return useQuery({
    queryKey: queryKeyFactory.availableMeetingTypes(tenantId, userId ?? ''),
    queryFn: () => {
      return userAvailability.getAvailableMeetingTypes(tenantId, userIdToUse)
    },
  })
}
